import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { getAuthHeader } from '../../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function useNomenclature() {
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [nomenclatures, setNomenclatures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(true);
  const [importing, setImporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [showEstimatorDialog, setShowEstimatorDialog] = useState(false);
  const [estimatorConfig, setEstimatorConfig] = useState({
    enabled: false,
    coefficient: '',
    min_ca: '',
    max_ca: ''
  });
  const [showNomenclatureEstimateursDialog, setShowNomenclatureEstimateursDialog] = useState(false);
  const [currentNomenclatureForEstimateurs, setCurrentNomenclatureForEstimateurs] = useState(null);
  const [formData, setFormData] = useState({
    collection_id: '',
    category: '',
    code: '',
    name: '',
    definition: '',
    exclusions: '',
    is_active: true,
    estimator: null
  });

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/nomenclature-collections`, {
        headers: getAuthHeader()
      });
      setCollections(response.data);
      
      // Sélectionner la première collection active par défaut
      const activeCollection = response.data.find(c => c.is_active);
      if (activeCollection && !selectedCollection) {
        setSelectedCollection(activeCollection);
        setFormData(prev => ({ ...prev, collection_id: activeCollection.id }));
      }
    } catch {
      toast.error('Erreur lors du chargement des collections');
    }
  };

  const fetchCategories = async () => {
    if (!selectedCollection) return;
    
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/categories?collection_id=${selectedCollection.id}&active_only=true`, 
        { headers: getAuthHeader() }
      );
      setCategories(response.data);
      
      // Définir la première catégorie par défaut pour le formulaire
      if (response.data.length > 0 && !formData.category) {
        setFormData(prev => ({ ...prev, category: response.data[0].name }));
      }
    } catch {
      toast.error('Erreur lors du chargement des catégories');
    }
  };

  const fetchNomenclatures = async () => {
    if (!selectedCollection) return;
    
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/nomenclatures?collection_id=${selectedCollection.id}`, 
        { headers: getAuthHeader() }
      );
      setNomenclatures(response.data);
    } catch {
      toast.error('Erreur lors du chargement des nomenclatures');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, []);

  useEffect(() => {
    if (selectedCollection) {
      fetchCategories();
      fetchNomenclatures();
    }
  }, [selectedCollection]);

  const handleCreate = async () => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/nomenclatures`,
        formData,
        { headers: getAuthHeader() }
      );
      
      toast.success('Nomenclature créée avec succès');
      setIsCreating(false);
      resetForm();
      fetchNomenclatures();
    } catch {
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdate = async (id) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/nomenclatures/${id}`,
        formData,
        { headers: getAuthHeader() }
      );
      
      toast.success('Nomenclature mise à jour');
      setEditingId(null);
      resetForm();
      fetchNomenclatures();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir archiver cette nomenclature ?')) {
      return;
    }
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/nomenclatures/${id}`,
        { headers: getAuthHeader() }
      );
      
      toast.success('Nomenclature archivée');
      fetchNomenclatures();
    } catch {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleReactivate = async (id) => {
    if (!window.confirm('Voulez-vous réactiver cette nomenclature et la remettre en ligne ?')) {
      return;
    }
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/nomenclatures/${id}`,
        { is_active: true },
        { headers: getAuthHeader() }
      );
      
      toast.success('Nomenclature réactivée et mise en ligne');
      fetchNomenclatures();
    } catch {
      toast.error('Erreur lors de la réactivation');
    }
  };

  const startEdit = (nomenclature) => {
    setEditingId(nomenclature.id);
    setFormData({
      collection_id: nomenclature.collection_id,
      category: nomenclature.category,
      code: nomenclature.code || '',
      name: nomenclature.name,
      definition: nomenclature.definition,
      exclusions: nomenclature.exclusions || '',
      is_active: nomenclature.is_active,
      estimator: nomenclature.estimator || null
    });
  };

  const resetForm = () => {
    setFormData({
      collection_id: selectedCollection?.id || '',
      category: categories.length > 0 ? categories[0].name : '',
      code: '',
      name: '',
      definition: '',
      exclusions: '',
      is_active: true,
      estimator: null
    });
  };

  // Fonction pour normaliser le texte (minuscules, sans accents, sans ponctuation)
  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
      .replace(/[^\w\s]/g, ''); // Supprime la ponctuation
  };

  const filteredNomenclatures = nomenclatures.filter(nom => {
    const matchesCategory = selectedCategory === 'all' || nom.category === selectedCategory;
    
    // Normaliser le terme de recherche
    const searchNormalized = normalizeText(searchTerm);
    
    // Normaliser et chercher dans nom, code et définition
    const matchesSearch = normalizeText(nom.name).includes(searchNormalized) ||
                         (nom.code && normalizeText(nom.code).includes(searchNormalized)) ||
                         (nom.definition && normalizeText(nom.definition).includes(searchNormalized));
    
    const matchesStatus = showArchived || nom.is_active;
    return matchesCategory && matchesSearch && matchesStatus;
  });

  const getCategoryColor = (categoryName) => {
    const colors = {
      'Gros Oeuvre': 'bg-blue-800 text-white border-blue-900 font-semibold',
      'Second Oeuvre': 'bg-green-800 text-white border-green-900 font-semibold',
      'Travaux Publics': 'bg-orange-700 text-white border-orange-800 font-semibold',
      'PIB': 'bg-purple-800 text-white border-purple-900 font-semibold'
    };
    return colors[categoryName] || 'bg-gray-800 text-white border-gray-900 font-semibold';
  };

  const getCategoryButtonStyle = (categoryName, isSelected) => {
    if (!isSelected) return '';
    
    const styles = {
      'Gros Oeuvre': 'bg-blue-700 hover:bg-blue-800 text-white border-blue-700 shadow-md',
      'Second Oeuvre': 'bg-green-700 hover:bg-green-800 text-white border-green-700 shadow-md',
      'Travaux Publics': 'bg-orange-700 hover:bg-orange-800 text-white border-orange-700 shadow-md',
      'PIB': 'bg-purple-700 hover:bg-purple-800 text-white border-purple-700 shadow-md'
    };
    return styles[categoryName] || 'bg-gray-700 hover:bg-gray-800 text-white shadow-md';
  };

  // Fonctions de sélection
  const toggleSelection = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    const allIds = new Set(filteredNomenclatures.map(n => n.id));
    setSelectedIds(allIds);
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    if (selectionMode) {
      setSelectedIds(new Set());
    }
  };

  // Fonctions d'export
  const handleExportCSV = async () => {
    try {
      const exportData = {
        selected_ids: selectionMode && selectedIds.size > 0 ? Array.from(selectedIds) : null,
        category: !selectionMode && selectedCategory !== 'all' ? selectedCategory : null,
        active_only: true
      };


      const response = await axios.post(
        `${BACKEND_URL}/api/nomenclatures/export/csv`,
        exportData,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const count = selectionMode ? selectedIds.size : filteredNomenclatures.length;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nomenclatures_${count}_items_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Export CSV réussi (${count} nomenclatures)`);
    } catch {
      toast.error('Erreur lors de l\'export CSV');
    }
  };

  const handleExportExcel = async () => {
    try {
      const exportData = {
        selected_ids: selectionMode && selectedIds.size > 0 ? Array.from(selectedIds) : null,
        category: !selectionMode && selectedCategory !== 'all' ? selectedCategory : null,
        active_only: true
      };


      const response = await axios.post(
        `${BACKEND_URL}/api/nomenclatures/export/excel`,
        exportData,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const count = selectionMode ? selectedIds.size : filteredNomenclatures.length;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nomenclatures_${count}_items_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Export Excel réussi (${count} nomenclatures)`);
    } catch {
      toast.error('Erreur lors de l\'export Excel');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/nomenclatures/template/csv`,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_nomenclatures.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template téléchargé');
    } catch {
      toast.error('Erreur lors du téléchargement du template');
    }
  };

  // Fonction d'import
  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setImporting(true);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('update_existing', 'false');
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/nomenclatures/import`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const result = response.data;
      
      if (result.errors && result.errors.length > 0) {
        toast.warning(
          `Import partiel: ${result.created} créées, ${result.updated} mises à jour, ${result.errors.length} erreurs`,
          { duration: 5000 }
        );
      } else {
        toast.success(`Import réussi: ${result.created} nomenclatures créées, ${result.updated} mises à jour`);
      }
      
      fetchCategories();
      fetchNomenclatures();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
      event.target.value = null; // Reset input
    }
  };


  return { collections, setCollections, selectedCollection, setSelectedCollection, nomenclatures, setNomenclatures, categories, setCategories, loading, setLoading, selectedCategory, setSelectedCategory, searchTerm, setSearchTerm, editingId, setEditingId, isCreating, setIsCreating, showArchived, setShowArchived, importing, setImporting, selectedIds, setSelectedIds, selectionMode, setSelectionMode, showEstimatorDialog, setShowEstimatorDialog, estimatorConfig, setEstimatorConfig, showNomenclatureEstimateursDialog, setShowNomenclatureEstimateursDialog, currentNomenclatureForEstimateurs, setCurrentNomenclatureForEstimateurs, formData, setFormData, fetchCollections, fetchCategories, fetchNomenclatures, handleCreate, handleUpdate, handleDelete, handleReactivate, startEdit, resetForm, normalizeText, filteredNomenclatures, getCategoryColor, getCategoryButtonStyle, toggleSelection, selectAll, deselectAll, toggleSelectionMode, handleExportCSV, handleExportExcel, handleDownloadTemplate, handleImport };
}
